import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { parseShareQr } from "../../../shared/qr";
import type { ItemMeta, SessionSnapshot } from "../../../shared/types";
import {
  api,
  ApiError,
  downloadUrl,
  errorMessage,
  formatBytes,
  formatCode,
  joinUrl,
} from "../api";
import { Icon } from "../components/icons";
import { ItemRow } from "../components/ItemRow";
import { Modal } from "../components/Modal";
import { Qr } from "../components/Qr";
import { QrScanner } from "../components/QrScanner";
import { UploadRow } from "../components/UploadRow";
import { useUploads } from "../hooks/useUploads";

interface Props {
  code: string;
  initialShowQr?: boolean;
  initialShowScanner?: boolean;
  onLeave: (message?: string) => void;
}

function clipboardFileName(file: File): File {
  if (file.name && file.name !== "image.png") return file;
  const stamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace("T", "_")
    .replaceAll(":", "-");
  const extension = file.type.split("/")[1]?.split("+")[0] ?? "png";
  return new File([file], `Zwischenablage_${stamp}.${extension}`, {
    type: file.type,
  });
}

export function Session({
  code,
  initialShowQr = false,
  initialShowScanner = false,
  onLeave,
}: Props) {
  const [snap, setSnap] = useState<SessionSnapshot | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    action?: { label: string; run: () => void };
  } | null>(null);
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());
  const knownIds = useRef<Set<string> | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showQr, setShowQr] = useState(initialShowQr);
  const [showScanner, setShowScanner] = useState(initialShowScanner);
  const [note, setNote] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showToast = useCallback(
    (message: string, action?: { label: string; run: () => void }) => {
      setToast({ message, action });
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(
        () => setToast(null),
        action ? 6000 : 5000,
      );
    },
    [],
  );

  const handleError = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        onLeave("Die Session ist abgelaufen oder wurde beendet.");
        return;
      }
      showToast(errorMessage(err));
    },
    [onLeave, showToast],
  );

  const onUploadCancelled = useCallback(
    () => showToast("Upload abgebrochen."),
    [showToast],
  );
  const { uploads, uploadFiles, cancelUpload } = useUploads(
    code,
    handleError,
    onUploadCancelled,
  );

  const addClip = useCallback(
    async (text: string) => {
      try {
        await api.addText(code, "clip", text);
        showToast("Text aus der Zwischenablage hinzugefügt.");
      } catch (err) {
        handleError(err);
      }
    },
    [code, handleError, showToast],
  );

  // Live-Updates per Server-Sent-Events
  useEffect(() => {
    knownIds.current = null;
    const source = new EventSource(`/api/sessions/${code}/events`);
    source.addEventListener("state", (event) => {
      const next = JSON.parse(event.data) as SessionSnapshot;
      setSnap(next);
      // Neu hinzugekommene Einträge kurz hervorheben (nicht beim ersten Laden)
      const ids = new Set(next.items.map((item) => item.id));
      if (knownIds.current) {
        const arrived = [...ids].filter((id) => !knownIds.current?.has(id));
        if (arrived.length > 0) {
          setFreshIds((prev) => new Set([...prev, ...arrived]));
          setTimeout(() => {
            setFreshIds((prev) => {
              const copy = new Set(prev);
              for (const id of arrived) copy.delete(id);
              return copy;
            });
          }, 2500);
        }
      }
      knownIds.current = ids;
    });
    source.addEventListener("ended", (event) => {
      source.close();
      const { reason } = JSON.parse(event.data) as { reason: string };
      onLeave(
        reason === "expired"
          ? "Die Session ist abgelaufen (1 h ohne Aktivität)."
          : "Die Session wurde beendet.",
      );
    });
    source.onerror = () => {
      // Bei Verbindungsabbruch prüfen, ob die Session noch existiert –
      // EventSource verbindet sich sonst automatisch neu.
      api.getSession(code).catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          source.close();
          onLeave("Die Session ist abgelaufen oder wurde beendet.");
        }
      });
    };
    return () => source.close();
  }, [code, onLeave]);

  // Drag & Drop auf die ganze Seite
  useEffect(() => {
    function onDragEnter(event: DragEvent) {
      if (!event.dataTransfer?.types.includes("Files")) return;
      event.preventDefault();
      dragDepth.current += 1;
      setDragActive(true);
    }
    function onDragOver(event: DragEvent) {
      event.preventDefault();
    }
    function onDragLeave(event: DragEvent) {
      event.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragActive(false);
    }
    function onDrop(event: DragEvent) {
      event.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      const files = Array.from(event.dataTransfer?.files ?? []);
      void uploadFiles(files);
    }
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [uploadFiles]);

  // Strg+V irgendwo auf der Seite legt den Inhalt der Zwischenablage ab
  useEffect(() => {
    function onPaste(event: ClipboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest("textarea, input")) return;
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length > 0) {
        event.preventDefault();
        void uploadFiles(files.map(clipboardFileName));
        return;
      }
      const text = event.clipboardData?.getData("text/plain");
      if (text?.trim()) {
        event.preventDefault();
        void addClip(text);
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [uploadFiles, addClip]);

  // Bild-Einträge als PNG in die Zwischenablage legen (Clipboard-API kann nur PNG)
  const copyImageItem = useCallback(
    (item: ItemMeta) => {
      const loadPng = async (): Promise<Blob> => {
        const response = await fetch(downloadUrl(code, item.id));
        const blob = await response.blob();
        if (blob.type === "image/png") return blob;
        const bitmap = await createImageBitmap(blob);
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
        return new Promise((resolve, reject) => {
          canvas.toBlob(
            (png) => (png ? resolve(png) : reject(new Error("Konvertierung fehlgeschlagen"))),
            "image/png",
          );
        });
      };
      navigator.clipboard
        .write([new ClipboardItem({ "image/png": loadPng() })])
        .then(() => showToast(`Bild „${item.name}“ kopiert.`))
        .catch(() =>
          showToast("Bild konnte nicht kopiert werden (Clipboard-API blockiert)."),
        );
    },
    [code, showToast],
  );

  // Strg+C ohne Textauswahl (und außerhalb von Eingabefeldern) kopiert
  // den neuesten Eintrag
  useEffect(() => {
    function onCopyShortcut(event: ClipboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest("textarea, input")) return;
      if (showQr || showScanner) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) return;
      const newest = snap?.items.at(-1);
      if (!newest) return;
      if (newest.kind !== "file") {
        event.preventDefault();
        event.clipboardData?.setData("text/plain", newest.text ?? "");
        showToast("Neuester Eintrag kopiert.");
      } else if (newest.mime?.startsWith("image/")) {
        event.preventDefault();
        copyImageItem(newest);
      } else {
        showToast(
          `Der neueste Eintrag ist eine Datei („${newest.name}“) – zum Übertragen den Download-Button nutzen.`,
        );
      }
    }
    document.addEventListener("copy", onCopyShortcut);
    return () => document.removeEventListener("copy", onCopyShortcut);
  }, [snap, showQr, showScanner, showToast, copyImageItem]);

  // Countdown für die Ablaufanzeige aktuell halten
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(timer);
  }, []);

  async function submitNote() {
    const text = note.trim();
    if (!text) return;
    try {
      await api.addText(code, "note", text);
      setNote("");
    } catch (err) {
      handleError(err);
    }
  }

  function onNoteKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submitNote();
    }
  }

  async function pasteFromClipboard() {
    try {
      if (navigator.clipboard?.read) {
        const clipboardItems = await navigator.clipboard.read();
        const files: File[] = [];
        let text = "";
        for (const item of clipboardItems) {
          const imageType = item.types.find((type) => type.startsWith("image/"));
          if (imageType) {
            const blob = await item.getType(imageType);
            files.push(clipboardFileName(new File([blob], "", { type: imageType })));
          } else if (item.types.includes("text/plain")) {
            text = await (await item.getType("text/plain")).text();
          }
        }
        if (files.length > 0) {
          await uploadFiles(files);
          return;
        }
        if (text.trim()) {
          await addClip(text);
          return;
        }
        showToast("Die Zwischenablage ist leer.");
        return;
      }
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text.trim()) await addClip(text);
        else showToast("Die Zwischenablage ist leer.");
        return;
      }
      throw new Error("Clipboard-API nicht verfügbar");
    } catch {
      showToast(
        "Zugriff auf die Zwischenablage nicht möglich – nutze stattdessen Strg+V / Cmd+V.",
      );
    }
  }

  async function copyText(item: ItemMeta) {
    try {
      await navigator.clipboard.writeText(item.text ?? "");
      showToast("In die Zwischenablage kopiert.");
    } catch {
      showToast("Kopieren nicht möglich (Clipboard-API blockiert).");
    }
  }

  async function removeItem(item: ItemMeta) {
    try {
      await api.deleteItem(code, item.id);
      const label =
        item.kind === "file" ? `„${item.name}“ gelöscht.` : "Eintrag gelöscht.";
      showToast(label, {
        label: "Rückgängig",
        run: () => {
          api.restoreItem(code, item.id).catch(handleError);
        },
      });
    } catch (err) {
      handleError(err);
    }
  }

  const handleScan = useCallback(
    (text: string) => {
      setShowScanner(false);
      const parsed = parseShareQr(text);
      if (!parsed) {
        showToast("Das ist kein Share-QR-Code.");
        return;
      }
      if (parsed.type === "pair") {
        api
          .completePairing(parsed.token, code)
          .then(() =>
            showToast("✅ Gerät verbunden – es tritt der Session automatisch bei."),
          )
          .catch(handleError);
        return;
      }
      if (parsed.code === code) {
        showToast("Das ist der QR-Code dieser Session.");
        return;
      }
      if (confirm(`Zur Session ${formatCode(parsed.code)} wechseln?`)) {
        location.href = `/j/${parsed.code}`;
      }
    },
    [code, handleError, showToast],
  );

  async function endSession() {
    if (!confirm("Session für alle verbundenen Geräte beenden? Alle Daten gehen verloren.")) {
      return;
    }
    try {
      await api.endSession(code);
    } catch (err) {
      handleError(err);
    }
  }

  function onFilesPicked(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    void uploadFiles(files);
  }

  const items = snap ? [...snap.items].reverse() : [];
  const minutesLeft = snap
    ? Math.max(0, Math.round((snap.expiresAt - now) / 60_000))
    : null;

  return (
    <main className="page">
      <header className="session-head">
        <div className="session-title">
          <h1>📦 Share</h1>
          <button
            className="code-chip"
            onClick={() => setShowQr(true)}
            title="QR-Code anzeigen"
          >
            {formatCode(code)}
            <Icon name="qr-code" className="chip-qr" />
          </button>
        </div>
        <div className="session-actions">
          {snap && minutesLeft !== null && (
            <span className="muted ttl">
              {snap.devices} {snap.devices === 1 ? "Gerät" : "Geräte"} · läuft
              ab in {minutesLeft} min
            </span>
          )}
          <button
            className="btn ghost"
            onClick={() => setShowScanner(true)}
            title="QR-Code scannen, z. B. um einen weiteren Rechner zu verbinden"
          >
            <Icon name="qr-code-scan" /> Scannen
          </button>
          <button className="btn ghost" onClick={() => onLeave()}>
            Verlassen
          </button>
          <button className="btn danger" onClick={endSession}>
            Beenden
          </button>
        </div>
      </header>

      <section className="composer card">
        <div
          className="dropzone"
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter") fileInputRef.current?.click();
          }}
        >
          <strong>Dateien hierher ziehen</strong> oder klicken zum Auswählen
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={onFilesPicked}
          />
        </div>

        <div className="note-row">
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={onNoteKeyDown}
            placeholder="Notiz schreiben … (Strg+Enter zum Speichern)"
            rows={2}
          />
          <div className="note-buttons">
            <button
              className="btn primary"
              onClick={submitNote}
              disabled={!note.trim()}
            >
              Notiz speichern
            </button>
            <button className="btn ghost" onClick={pasteFromClipboard}>
              <Icon name="clipboard-plus" /> Zwischenablage einfügen
            </button>
          </div>
        </div>
      </section>

      {uploads.map((job) => (
        <UploadRow key={job.id} job={job} onCancel={cancelUpload} />
      ))}

      <section className="items">
        {items.length === 0 && uploads.length === 0 && (
          <div className="empty">
            Noch nichts hier. Zieh Dateien auf die Seite, schreib eine Notiz
            oder drücke Strg+V, um etwas aus der Zwischenablage abzulegen.
            Strg+C kopiert jederzeit den neuesten Eintrag.
          </div>
        )}
        {items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            sessionCode={code}
            fresh={freshIds.has(item.id)}
            onCopy={copyText}
            onDelete={removeItem}
          />
        ))}
      </section>

      {snap && snap.items.length > 0 && (
        <footer className="session-foot muted">
          {snap.items.length}{" "}
          {snap.items.length === 1 ? "Eintrag" : "Einträge"} ·{" "}
          {formatBytes(snap.totalBytes)}
        </footer>
      )}

      {dragActive && (
        <div className="drop-overlay">
          <div className="drop-overlay-inner">
            <Icon name="download" size={32} /> Loslassen zum Hochladen
          </div>
        </div>
      )}

      {toast && (
        <div className="toast">
          <span>{toast.message}</span>
          {toast.action && (
            <button
              className="toast-action"
              onClick={() => {
                toast.action?.run();
                clearTimeout(toastTimer.current);
                setToast(null);
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}

      {showQr && (
        <Modal title="Gerät hinzufügen" onClose={() => setShowQr(false)}>
          <div className="qr-modal">
            <Qr value={joinUrl(code)} size={260} />
            <p className="muted">
              Mit dem Handy scannen – es tritt der Session sofort bei. Oder auf
              dem anderen Gerät <code>{location.host}</code> öffnen und den
              Code <strong>{formatCode(code)}</strong> eingeben.
            </p>
          </div>
        </Modal>
      )}

      {showScanner && (
        <Modal title="QR-Code scannen" onClose={() => setShowScanner(false)}>
          <QrScanner onResult={handleScan} />
          <p className="muted">
            Scanne den QR-Code auf der Startseite eines anderen Geräts, um es
            mit dieser Session zu verbinden.
          </p>
        </Modal>
      )}
    </main>
  );
}
